import useSWR from 'swr';
import { transformBlock } from 'helpers';
import { useTenantConfig, useServices } from 'contexts';
import { useAuth } from '@teko/auth-react';
import {
  ObjectMap,
  PersonalizeBlockParams,
  DiscoverySearchResponse,
  InfiniteDiscoverySearchResponse,
  DiscoveryProductResponse,
  PersonalizeBlocksResponse,
  DiscoveryProduct,
  SkusResponse,
  ProductInfoCategory,
} from 'types';
import {
  ComboDetailPayload,
  Combo as ICombo,
  useDiscoverySearch as useEcomDiscoverySearch,
  useInfiniteDiscoverySearch as useEcomInfiniteDiscoverySearch,
  useDiscoveryProduct as useEcomDiscoveryProduct,
  usePersonalizeBlocks as useEcomPersonalizeBlocks,
  useSkus as useEcomSkus,
} from '@teko/discovery';
import get from 'lodash.get';
import { alcoholicCatCodes, ippAlcoholicCatCodes } from 'constants/categories';

const checkAlcoholicProduct = (product: DiscoveryProduct) => {
  const { tenant } = useTenantConfig();
  if (!product) return product;

  const ipp = 'ipp';
  const catCodes = tenant === ipp ? ippAlcoholicCatCodes : alcoholicCatCodes;
  const alcoholicCatKey = tenant === ipp ? 'categories' : 'masterCategories';
  const sellerCatCodes: ProductInfoCategory[] = get(
    product,
    `productInfo.${alcoholicCatKey}`,
    [],
  );

  return {
    ...product,
    isAlcoholicCategory: sellerCatCodes
      .map((masterCategory) => masterCategory.code)
      .some((code) => catCodes.includes(code)),
  };
};

export const useDiscoverySearch = (
  params: ObjectMap,
  isSearchNoKeyword?: boolean,
): DiscoverySearchResponse => {
  const { location } = useTenantConfig();
  const { discoveryService, discoveryPos365Service } = useServices();
  const { result, ...rest } = useEcomDiscoverySearch(
    params?.pos365StoreCode ? discoveryPos365Service : discoveryService,
    params,
    location,
    isSearchNoKeyword,
  );
  return {
    result: {
      ...result,
      products: result?.products,
    },
    ...rest,
  };
};

export const useInfiniteDiscoverySearch = (
  params: ObjectMap,
): InfiniteDiscoverySearchResponse => {
  const { location } = useTenantConfig();
  const { discoveryService, discoveryPos365Service } = useServices();
  const { products, ...rest } = useEcomInfiniteDiscoverySearch(
    params?.pos365StoreCode ? discoveryPos365Service : discoveryService,
    params,
    location,
  );
  return {
    products: products,
    ...rest,
  };
};

export const useDiscoveryProduct = (sku: string): DiscoveryProductResponse => {
  const { terminal } = useTenantConfig();
  const { discoveryService } = useServices();
  const {
    result: { product },
    ...rest
  } = useEcomDiscoveryProduct(discoveryService, sku, terminal);
  return {
    result: {
      product: checkAlcoholicProduct(product),
    },
    ...rest,
  };
};

export const usePersonalizeBlocks = (
  blockParams: PersonalizeBlockParams,
): PersonalizeBlocksResponse => {
  const { terminal } = useTenantConfig();
  const { user } = useAuth();
  const { discoveryService } = useServices();
  return useEcomPersonalizeBlocks(
    discoveryService,
    blockParams,
    terminal,
    transformBlock,
    user,
  );
};

export const useSkus = (skus: string[]): SkusResponse => {
  const { terminal, location } = useTenantConfig();
  const { discoveryService } = useServices();
  return useEcomSkus(discoveryService, skus, terminal, location);
};

export const useRecommendationProductsData = (sku: string) => {
  const { terminal } = useTenantConfig();
  const { discoveryService } = useServices();
  const { data, error } = useSWR(
    !!sku && !!terminal
      ? [
          `${discoveryService.getBaseUrl}/products_recommendation`,
          sku,
          terminal,
        ]
      : null,
    async (_: string, sku: string, terminal: string) => {
      return await discoveryService.getRecommendationProducts(sku, terminal);
    },
    {
      revalidateOnFocus: false,
    },
  );
  return {
    result: {
      productSets: data?.result?.productSets || [],
    },
    isLoading: !error && !data,
    isError: error,
  };
};

export const useRecommendationCombosData = (sku: string) => {
  const { terminal, location } = useTenantConfig();
  const { discoveryService } = useServices();
  const { data, error } = useSWR(
    !!sku && !!terminal
      ? [
          `${discoveryService.getBaseUrl}/products_combo`,
          sku,
          terminal,
          location,
        ]
      : null,
    async (_: string, sku: string, terminal: string, location: string) => {
      return await discoveryService.getRecommendationCombos(sku, {
        terminalCode: terminal,
        location,
      });
    },
    {
      revalidateOnFocus: false,
    },
  );
  return {
    result: {
      combos: data?.result?.combos || [],
    },
    isLoading: !error && !data,
    isError: error,
  };
};

export const useRecommendationComboDetail = (
  payloadCombo: ComboDetailPayload,
  call = false,
) => {
  const { terminal, location } = useTenantConfig();
  const { discoveryService } = useServices();
  const skus = (payloadCombo?.comboLineItems || []).map((el) => el.sku);

  const { data, error } = useSWR<ICombo>(
    payloadCombo && call
      ? [
          `${discoveryService.getBaseUrl}/detail-combo`,
          JSON.stringify({ ...payloadCombo, terminalCode: terminal, location }),
        ]
      : null,
    (_: any): Promise<ICombo> => {
      return discoveryService
        .getDetailComboRecommendation({
          ...payloadCombo,
          terminalCode: terminal,
          location,
        })
        .then((res) => res?.result?.combo);
    },
    { revalidateOnFocus: false },
  );

  const reorderProductInCombo = () => {
    const { products } = data;
    const newProducts = skus.map((el: string) => {
      const product = products.find((p) => p.product.productInfo.sku === el);
      return product;
    });
    return {
      ...data,
      products: newProducts,
    };
  };

  return {
    result: {
      combo: data ? reorderProductInCombo() : null,
    },
    isLoading: !error && !data,
    isError: error,
  };
};
